import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import ScrollReveal from "./ScrollReveal";

// Import stain images
import naturalStain from "@/assets/stains/natural.jpg";
import classicGrayStain from "@/assets/stains/classic-gray.jpg";
import weatheredOakStain from "@/assets/stains/weathered-oak.jpg";
import earlyAmericanStain from "@/assets/stains/early-american.jpg";
import provincialStain from "@/assets/stains/provincial.jpg";
import specialWalnutStain from "@/assets/stains/special-walnut.jpg";
import darkWalnutStain from "@/assets/stains/dark-walnut.jpg";

interface Stain {
  name: string;
  image: string;
  isPopular?: boolean;
}

const stains: Stain[] = [
  {
    name: "Natural",
    image: naturalStain,
  },
  {
    name: "Classic Gray",
    image: classicGrayStain,
  },
  {
    name: "Weathered Oak",
    image: weatheredOakStain,
  },
  {
    name: "Early American",
    image: earlyAmericanStain,
    isPopular: true,
  },
  {
    name: "Provincial",
    image: provincialStain,
  },
  {
    name: "Special Walnut",
    image: specialWalnutStain,
    isPopular: true,
  },
  {
    name: "Dark Walnut",
    image: darkWalnutStain,
  },
];

const StainSelectionSection = () => {
  const [selectedStain, setSelectedStain] = useState<Stain | null>(null);

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <ScrollReveal>
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              DuraSeal Stain Options
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg leading-relaxed">
              We use DuraSeal premium wood stains for consistent color and long-lasting results. 
              Below are some of the most popular stain options we work with to help you get a feel 
              for tone and style.
            </p>
            <p className="text-primary font-medium mt-4">
              Final color is always confirmed on-site with real samples.
            </p>
          </div>
        </ScrollReveal>

        {/* Stain Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 md:gap-6 max-w-6xl mx-auto mb-8">
          {stains.map((stain, index) => (
            <ScrollReveal key={stain.name} delay={index * 0.05}>
              <button
                onClick={() => setSelectedStain(stain)}
                className="group w-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-lg"
                aria-label={`View ${stain.name} stain`}
              >
                <div className="relative">
                  {/* Wood Swatch */}
                  <div className="aspect-square overflow-hidden rounded-lg shadow-md border border-border/50 transition-all duration-300 group-hover:shadow-xl group-hover:scale-105">
                    <img 
                      src={stain.image} 
                      alt={`${stain.name} stain on red oak`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Popular Badge */}
                  {stain.isPopular && (
                    <Badge 
                      className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] px-2 py-0.5 shadow-sm"
                    >
                      Popular
                    </Badge>
                  )}
                </div>
                
                {/* Stain Name */}
                <p className="mt-3 text-sm font-medium text-foreground text-center group-hover:text-primary transition-colors">
                  {stain.name}
                </p>
              </button>
            </ScrollReveal>
          ))}
        </div>

        {/* Wood species note */}
        <ScrollReveal>
          <p className="text-center text-sm text-muted-foreground mb-4 italic">
            Samples shown on Red Oak
          </p>
          <p className="text-center text-sm text-muted-foreground mb-8">
            Looking for more options?{" "}
            <a 
              href="https://www.duraseal.com/stain-gallery/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              View all DuraSeal stain colors
            </a>
          </p>
        </ScrollReveal>

        {/* Disclaimer */}
        <ScrollReveal>
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-muted-foreground text-sm leading-relaxed border-t border-border pt-8">
              Online stain colors are for reference only. Actual stain color will vary based on 
              wood species, age, grain, sanding process, and lighting. We always confirm stain 
              color on-site using real samples before finishing.
            </p>
          </div>
        </ScrollReveal>
      </div>

      {/* Stain Preview Dialog */}
      <Dialog open={!!selectedStain} onOpenChange={() => setSelectedStain(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-foreground">
              {selectedStain?.name}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              DuraSeal® Premium Wood Stain on Red Oak
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Large Swatch Preview */}
            {selectedStain && (
              <div className="w-full aspect-square overflow-hidden rounded-lg shadow-lg border border-border/50">
                <img 
                  src={selectedStain.image} 
                  alt={`${selectedStain.name} stain on red oak - large preview`}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {selectedStain?.isPopular && (
              <Badge className="mt-4 bg-primary text-primary-foreground">
                Popular Choice
              </Badge>
            )}
            
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Color appearance varies based on wood species, grain pattern, age of the wood, 
              and room lighting. We recommend confirming your final stain choice with an 
              on-site sample before we begin the finishing process.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default StainSelectionSection;
